import { Alert, Button, Modal, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';

import {
  LONG_WAIT_HINTS_PROVIDER,
  PROVIDER_SETTINGS_WINDOW_FEATURES,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/constants';
import {
  useLongWaitHint,
  useProviderAuth,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/hooks';
import {
  AuthStepState,
  ProviderWindowState,
  type AuthStepInfo,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';
import { AuthStepView } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/AuthStepView';
import { LongWaitNotice } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/LongWaitNotice';

import type { FC } from 'react';

const { Text } = Typography;

export interface ProviderAuthContentProps {
  /** Название канала для текстов («ВКонтакте», «Instagram») */
  channelTitle: string;
  authUrl: string | null;
  stepInfo: AuthStepInfo;
  isActive: boolean;
  /** У канала есть вход по логину и паролю на своей странице (Instagram) */
  hasEmbeddedLogin?: boolean;
}

/**
 * Архетип «вход у провайдера» (ВКонтакте, Instagram — task/10731): экран
 * входа рисует провайдер в отдельном окне браузера, CRM показывает ожидание
 * и ловит успех поллингом каркаса.
 */
export const ProviderAuthContent: FC<ProviderAuthContentProps> = ({
  channelTitle,
  authUrl,
  stepInfo,
  isActive,
  hasEmbeddedLogin = false,
}) => {
  const { windowState, openProviderWindow } = useProviderAuth({
    authUrl,
    stepInfo,
    isActive,
  });

  // «Серый» вход (логин и пароль) живёт на странице шлюза: переносить форму
  // в CRM не стали — открываем страницу во фрейме поверх шага «Авторизация»
  // (issue/10877). Вход у провайдера остаётся отдельным окном браузера:
  // OAuth Meta во встроенном фрейме не работает.
  const [isLoginFrameOpen, setIsLoginFrameOpen] = useState(false);

  // Ожидание входа в окне провайдера тоже затягивается — объясняем, что
  // происходит, вместо молчаливого спиннера (task/10903).
  const isWaitingProvider =
    isActive &&
    windowState === ProviderWindowState.Waiting &&
    stepInfo.state !== AuthStepState.Success &&
    stepInfo.state !== AuthStepState.Error;
  const { hint: providerHint } = useLongWaitHint({
    isWaiting: isWaitingProvider,
    hints: LONG_WAIT_HINTS_PROVIDER,
  });

  // Поллинг каркаса поймал успешный вход — фрейм больше не нужен.
  useEffect(() => {
    if (stepInfo.state === AuthStepState.Success) {
      setIsLoginFrameOpen(false);
    }
  }, [stepInfo.state]);

  const loginFrame = (
    <Modal
      open={isLoginFrameOpen}
      onCancel={() => setIsLoginFrameOpen(false)}
      title={`Вход по логину и паролю — ${channelTitle}`}
      footer={null}
      className="oauth-settings-modal"
      destroyOnClose
    >
      {authUrl ? (
        <iframe
          src={authUrl}
          className="oauth-settings-frame"
          title={`Вход ${channelTitle}`}
          data-testid="connect-auth-login-frame"
        />
      ) : (
        <Spin />
      )}
    </Modal>
  );

  const loginFrameButton = hasEmbeddedLogin && Boolean(authUrl) && (
    <Button
      onClick={() => setIsLoginFrameOpen(true)}
      data-testid="connect-auth-login-frame-open"
    >
      Войти по логину и паролю
    </Button>
  );

  // Страница канала открывается отдельным окном пошире окна входа: на ней
  // формы настроек, а не один экран авторизации.
  const openSettingsWindow = () => {
    if (authUrl) {
      window.open(authUrl, '_blank', PROVIDER_SETTINGS_WINDOW_FEATURES);
    }
  };

  // Интеграция уже работает — окно открыли по шестерёнке. Кроме входа на
  // странице канала живут «серая» схема Instagram и синхронизация истории
  // (issue/10881), поэтому с финального экрана туда нужен путь.
  if (stepInfo.state === AuthStepState.Success) {
    return (
      <div className="connect-integration-modal__provider">
        <AuthStepView stepInfo={stepInfo} />
        {Boolean(authUrl) && (
          <Button
            onClick={openSettingsWindow}
            data-testid="connect-auth-provider-settings"
          >
            {`Открыть настройки ${channelTitle}`}
          </Button>
        )}
      </div>
    );
  }

  if (stepInfo.state === AuthStepState.Error) {
    return <AuthStepView stepInfo={stepInfo} />;
  }

  if (!authUrl) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Ссылка на вход не найдена"
        description="Закройте окно и откройте настройку интеграции ещё раз."
      />
    );
  }

  const isClosedWithoutSuccess = windowState === ProviderWindowState.Closed;

  return (
    <div className="connect-integration-modal__provider">
      {isClosedWithoutSuccess ? (
        <Alert
          type="warning"
          showIcon
          message="Вход не завершён"
          description={`Окно ${channelTitle} закрылось раньше, чем вход подтвердился. Откройте окно ещё раз и завершите вход.`}
        />
      ) : (
        <div className="connect-integration-modal__provider-waiting">
          <Spin />
          <Text strong>{`Продолжите вход в окне ${channelTitle}`}</Text>
          <Text type="secondary">Ожидаем завершения входа…</Text>
          {providerHint ? <LongWaitNotice hint={providerHint} /> : null}
        </div>
      )}
      <Button
        type={isClosedWithoutSuccess ? 'primary' : 'default'}
        onClick={openProviderWindow}
      >
        {windowState === ProviderWindowState.Idle
          ? 'Открыть окно входа'
          : 'Открыть окно входа ещё раз'}
      </Button>
      {loginFrameButton}
      {loginFrame}
    </div>
  );
};
